/**
 * Turning a finished capture into the one thing the desk receives: a number, a quality, and codes.
 *
 * Everything else the capture produced — frames, landmarks, per-frame geometry, the eye band — has
 * already gone out of scope by the time this runs. What leaves the device is the total PD as a
 * decimal string, four quality figures, the algorithm's name and a coarse device class.
 */
import { ASSISTED_ALGORITHM } from '@optiflow/pd-vision';
import { MeasurementAttemptOutcome, type CaptureDeviceClass } from '@optiflow/types';
import type { AssistedAttempt } from './vision/useAssistedCapture';
import type { CaptureCandidate } from './captureClient';

/** The iris diameter is assumed rather than measured in frame, which is what makes the result reference-only. */
const CALIBRATION = 'IRIS_REFERENCE' as const;

export function candidateFrom(
  attempt: AssistedAttempt,
  deviceClass: CaptureDeviceClass,
  submissionId: string = crypto.randomUUID(),
): CaptureCandidate | null {
  const { decision, signals } = attempt;
  if (decision.state !== 'RESULT' || decision.totalPdMm === null || !signals) return null;

  return {
    submissionId,
    totalPdMm: decision.totalPdMm,
    calibrationMethod: CALIBRATION,
    algorithmVersion: ASSISTED_ALGORITHM,
    landmarkQualityBp: signals.landmarkBasisPoints,
    calibrationQualityBp: signals.calibrationBasisPoints,
    poseQualityBp: signals.poseBasisPoints,
    stabilityQualityBp: signals.stabilityBasisPoints,
    overallQualityBp: signals.overallBasisPoints,
    acceptedFrameCount: signals.acceptedFrameCount,
    deviceClass,
  };
}

/** Why an attempt produced nothing, as one of the outcomes the desk already counts. No reading is sent. */
export function outcomeFrom(attempt: AssistedAttempt): MeasurementAttemptOutcome {
  return attempt.decision.reason === 'TOO_FEW_GOOD_FRAMES'
    ? MeasurementAttemptOutcome.TIMED_OUT
    : MeasurementAttemptOutcome.QUALITY_TOO_LOW;
}
